// BatchCloning.service.ts
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type CloningScheduleStatus = 'Scheduled' | 'Done' | 'Failed';

export type CloningScheduleItemCategory =
  | 'Proposal'
  | 'Invoice'
  | 'Email'
  | 'Payment';

export type CloningScheduleItem = {
  scheduleId: string;
  source: string;
  projectName: string;
  itemCategory: CloningScheduleItemCategory | string;
  itemId: string;
  status: CloningScheduleStatus | string;
  scheduledFor: string;
};

export type CloningScheduleResponse = {
  items: CloningScheduleItem[];
  count: number;
};

@Injectable({
  providedIn: 'root',
})

export class BatchCloningService {
  constructor(private http: HttpClient) { }

  getCloningProjects(type: string, year?): Observable<any> {
    let url = `/cloning/candidates/${type}`;
    if (year) {
      url += `?year=${year}`;
    }
    return this.http.get<any>(url);
  }

  postCloningJobs(payload: any): Observable<any> {
    return this.http.post<any>(`/cloning/jobs`, payload);
  }

  getCloningJobsList(startDate?: string, endDate?: string): Observable<any> {
    const params: Record<string, string> = {};

    if (startDate) {
      params['startDate'] = startDate;
    }

    if (endDate) {
      params['endDate'] = endDate;
    }

    return this.http.get<any>(`/cloning/jobs/list`, { params });
  }

  getCloningProjectsByJobId(jobId: string): Observable<any> {
    return this.http.get<any>(`/cloning/jobs/${jobId}`);
  }

  retryCloningJobByJobId(jobId: string): Observable<any> {
    return this.http.post<any>(`/cloning/jobs/${jobId}/retry`, {});
  }


  createCloningSchedule(payload: any): Observable<any> {
    return this.http.post<any>(`/cloning/schedule`, payload);
  }

  getCloningJobs(): Observable<any> {
    return this.http.get<any>(`/cloning/jobs/history`);
  }

  checkCloningProgress(): Observable<any> {
    return this.http.get<any>(`/cloning/jobs/inProgress`);
  }

  retryCloningJob(jobId: string): Observable<any> {
    return this.http.post<any>(`/cloning/retry/${jobId}`, {});
  }

  getCloningSchedule(params?: {
    offset?: number;
    limit?: number;
    searchTerm?: string;
  }): Observable<any> {
    return this.http.get(`/cloning/schedule`, {
      params: {
        ...(params?.offset !== undefined
          ? { offset: params.offset }
          : {}),
        ...(params?.limit !== undefined
          ? { limit: params.limit }
          : {}),
        ...(params?.searchTerm
          ? { searchTerm: params.searchTerm } 
          : {}),
      },
    });
  }

  getCloningScheduleByDate(params?: {
    month?: number;
    year?: number;
  }): Observable<any> {
    return this.http.get(`/schedule/calendar`, {
      params: {
        ...(params?.month !== undefined
          ? { month: params.month }
          : {}),
        ...(params?.year !== undefined
          ? { year: params.year }
          : {}),
      },
    });
  }


  retryCloningSchedule(scheduleId: string): Observable<any> {
    return this.http.post<any>(
      `/cloning/schedule/${encodeURIComponent(scheduleId)}/retry`,
      {},
    );
  }

  rescheduleCloningSchedule(
    scheduleId: string,
    payload: { sendAt: string },
  ): Observable<any> {
    return this.http.post<any>(
      `/cloning/schedule/${encodeURIComponent(scheduleId)}/reschedule`,
      payload,
    );
  }

  cancelCloningSchedule(scheduleId: string): Observable<any> {
    return this.http.post<any>(
      `/cloning/schedule/${encodeURIComponent(scheduleId)}/cancel`,
      {},
    );
  }

}
